import { Alert, Center, CloseButton, Combobox, Flex, Loader, Stack, Text } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { BaseFieldSelectStore } from '@/stores/local/shared/form/FieldSelectStore';

import s from './OptionsDropdown.module.scss';

type OptionsDropdownProps = {
  className?: string;
  store: BaseFieldSelectStore;
  withSearch?: boolean;
  hideSubtitle?: boolean;
};

const OptionsDropdown: React.FC<OptionsDropdownProps> = ({
  className,
  store,
  withSearch = false,
  hideSubtitle = false,
}) => {
  const { ref, inView } = useInView();

  React.useEffect(() => {
    if (inView) {
      if (store.options.length === 0) {
        store.load();
      }
    }
  }, [store, inView]);

  const onSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      store.setSearchValue(event.currentTarget.value);
    },
    [store],
  );

  return (
    <Combobox.Dropdown className={cn(className, s.root)}>
      {withSearch && (
        <Combobox.Search
          classNames={{
            input: s.root__search,
          }}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          value={store.searchValue}
          onChange={onSearchChange}
          placeholder="Поиск"
          rightSectionPointerEvents={store.searchValue.length > 0 ? 'auto' : undefined}
          rightSection={store.searchValue.length > 0 && <CloseButton size="sm" onClick={store.resetSearch} />}
        />
      )}
      <Combobox.Options className={s.root__options}>
        {store.meta.isError && store.options.length === 0 && (
          <Alert variant="light" color="red">
            Произошла ошибка, попробуйте повторить позже
          </Alert>
        )}
        {store.meta.isLoaded && store.options.length === 0 && <Combobox.Empty>Данные не найдены</Combobox.Empty>}

        <Stack gap={2}>
          {store.options.map((item) => {
            return (
              <Combobox.Option
                className={cn(s.root__option, store.valueKeysSet.has(item.key) && s.root__option_selected)}
                selected={store.valueKeysSet.has(item.key)}
                value={item.key}
                key={item.key}
              >
                <Flex gap="sm" align="center" justify="space-between">
                  <div className={s['root__option-content']}>
                    <Text className={s['root__option-title']}>{item.value}</Text>
                    {!hideSubtitle && item?.subtitle && (
                      <Text size="xs" className={s['root__option-subtitle']}>
                        {item.subtitle}
                      </Text>
                    )}
                  </div>
                  {store.valueKeysSet.has(item.key) && <IconCircleCheck className={s.root__icon} size={16} />}
                </Flex>
              </Combobox.Option>
            );
          })}
        </Stack>

        <div ref={ref} className={s.root__sentinel} />

        {(store.meta.isLoading || store.meta.isInitialMetaState) && (
          <Center mih={50}>
            <Loader size="xs" />
          </Center>
        )}
      </Combobox.Options>
    </Combobox.Dropdown>
  );
};

export default observer(OptionsDropdown);
